import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Modal,
  Label,
  TextInput,
  Radio,
  FileInput,
} from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { HiOutlineTrash } from "react-icons/hi";
import {
  fetchQuickSearch,
  createQuickSearch,
  deleteQuickSearch,
} from "../../redux/slices/remote-config";
import { Breadcrumb, NavBar, SideNav } from "../../components";
import { createRandomString, capitalize } from "../../helpers/handlers/methods";

const RemoteConfig = () => {
  const dispatch = useDispatch();
  const { searches, pageLoading, buttonLoading } = useSelector(
    (state) => state.remoteConfig
  );
  const [showModal, setShowModal] = useState({ show: false, category: null });
  const [newCategory, setNewCategory] = useState("");
  const [style, setStyle] = useState("tag");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [deletingItems, setDeletingItem] = useState([]);

  useEffect(() => {
    const didLoad = async () => {
      dispatch(fetchQuickSearch());
    };

    didLoad();
  }, [dispatch]);

  const onCloseModal = () => {
    setShowModal({ show: false, category: null });
  };

  const handleRadioButtonSelected = (e) => {
    let style = e.target.id;
    setStyle(style);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (style === "tag") {
      const payload = {
        data: {
          type: style,
          title: title,
          category: showModal.category || newCategory,
        },
      };
      await dispatch(createQuickSearch(payload));
    } else if (style === "suggestion") {
      const payload = {
        data: {
          type: style,
          title: title,
          category: showModal.category || newCategory,
          description: description,
        },
        image,
      };
      await dispatch(createQuickSearch(payload));
      console.log("Create payload", payload);
    }

    setShowModal({ show: false });
    console.log("Submit form now");
  };

  const handleDeleteItem = async (e, id) => {
    e.preventDefault();
    setDeletingItem((prev) => [...prev, id]);
    await dispatch(deleteQuickSearch(id));
  };

  const Tags = ({ list }) => {
    return (
      <div className="flex gap-2 flex-wrap mt-4" key={createRandomString(10)}>
        {list.map((item) => {
          const isDeleting = deletingItems.includes(item.id);
          return (
            <div
              className="border rounded-full px-2 flex content-center gap-1 group"
              key={createRandomString(10)}
            >
              <Label
                key={createRandomString(10)}
                className={`${isDeleting && "text-gray-300"}`}
              >
                {item.title}
              </Label>
              <button
                className={`hidden group-hover:block ${
                  isDeleting && "text-gray-200"
                }`}
                onClick={(e) => handleDeleteItem(e, item.id)}
                disabled={isDeleting}
              >
                <HiOutlineTrash />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const Suggestions = ({ list }) => {
    return (
      <div
        className="flex gap-4 flex-wrap mt-4 flex-col"
        key={createRandomString(10)}
      >
        {list.map((item) => {
          const isDeleting = deletingItems.includes(item.id);
          return (
            <div
              className="flex gap-2 items-center group"
              key={createRandomString(10)}
            >
              <button
                className={`hidden group-hover:block ${
                  isDeleting && "text-gray-200"
                }`}
                onClick={(e) => handleDeleteItem(e, item.id)}
                disabled={isDeleting}
              >
                <HiOutlineTrash />
              </button>
              <img
                src={item.image}
                alt="search item icon"
                className={`h-8 w-8 border rounded-full ${
                  isDeleting && "opacity-30"
                }`}
                key={createRandomString(10)}
              />
              <Label
                className={`text-lg font-medium ${
                  isDeleting && "text-gray-200"
                }`}
                key={createRandomString(10)}
              >
                {item.title}
              </Label>
              <Label
                className="text-gray-500 text-xs"
                key={createRandomString(10)}
              >
                {item.description}
              </Label>
            </div>
          );
        })}
      </div>
    );
  };

  const QuickSearch = ({ group }) => {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-4 p-4">
          <div className="flex items-center">
            <Label className="font-display text-xl font-bold grow">
              {capitalize(group.title)}
            </Label>
            <Button
              color="purple"
              size="sm"
              onClick={() => {
                setStyle(group.style);
                setShowModal({ show: true, category: group.title });
              }}
            >
              Add New
            </Button>
          </div>
          <hr className="mt-2" />
          <div className=""></div>
          {group.style === "tag" && <Tags list={group.list} />}
          {group.style === "suggestion" && <Suggestions list={group.list} />}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="antialiased bg-gray-50 dark:bg-gray-900">
        <NavBar />
        <SideNav />
        <main className="p-4 md:ml-64 h-auto pt-20">
          <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
            <Breadcrumb />
          </div>
          <Button
            color="purple"
            className="mb-6"
            onClick={() => setShowModal({ show: true, category: null })}
          >
            Create Category
          </Button>
          <Modal show={showModal.show} size="lg" onClose={onCloseModal} popup>
            <Modal.Header />
            <form onSubmit={handleSubmit}>
              <Modal.Body>
                <div className="space-y-6">
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                    {showModal.category
                      ? `Add new ${capitalize(showModal.category)}`
                      : "New Quick Search Category"}
                  </h3>
                  {!showModal.category && (
                    <div>
                      <div className="mb-2 block">
                        <Label htmlFor="category" value="Category" />
                      </div>
                      <TextInput
                        id="category"
                        type="text"
                        placeholder="Enter Category"
                        onChange={(e) => setNewCategory(e.target.value)}
                        required
                      />
                    </div>
                  )}

                  {!showModal.category && (
                    <div>
                      <div className="mb-2 block">
                        <Label htmlFor="Style" value="Display Style" />
                      </div>
                      <fieldset className="flex max-w-md flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Radio
                            id="tag"
                            name="style"
                            value="Tag"
                            checked={style === "tag"}
                            onClick={handleRadioButtonSelected}
                          />
                          <Label htmlFor="tag">Tag</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Radio
                            id="suggestion"
                            name="suggestion"
                            value="List"
                            checked={style === "suggestion"}
                            onClick={handleRadioButtonSelected}
                          />
                          <Label htmlFor="suggestion">List</Label>
                        </div>
                      </fieldset>
                    </div>
                  )}

                  <div className="flex gap-4">
                    <div className="grow">
                      <div className="mb-2 block">
                        <Label htmlFor="searchText" value="Search Text" />
                      </div>
                      <TextInput
                        id="category"
                        type="text"
                        placeholder="Enter Search Text"
                        onChange={(e) => setTitle(e.target.value)}
                        required
                      />
                    </div>
                    {style === "suggestion" && (
                      <div className="grow">
                        <div className="mb-2 block">
                          <Label
                            htmlFor="secondaryText"
                            value="Secondary Text (Optional)"
                          />
                        </div>
                        <TextInput
                          id="secondaryText"
                          type="text"
                          placeholder="Enter Secondary Text"
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                  {style === "suggestion" && (
                    <div>
                      <div id="fileUpload" className="max-w-md">
                        <div className="mb-2 block">
                          <Label htmlFor="file" value="Upload Icon" />
                        </div>
                        <FileInput
                          id="file"
                          accept="image/*"
                          onChange={(e) => setImage(e.target.files[0])}
                          required
                        />
                      </div>
                    </div>
                  )}
                  <div className="pt-4">
                    <Button
                      type="submit"
                      color="purple"
                      className="w-full"
                      isProcessing={buttonLoading}
                      disabled={buttonLoading}
                      processingSpinner={
                        <AiOutlineLoading className="h-6 w-6 animate-spin" />
                      }
                    >
                      {showModal.category ? "Add" : "Create"}
                    </Button>
                  </div>
                </div>
              </Modal.Body>
            </form>
          </Modal>

          {/* Skeleton loader */}
          {pageLoading ? (
            <div className="grid grid-cols-2 gap-4 animate-pulse">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-4 p-4">
                <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-40" />
                <hr className="mt-2" />
                <div className="flex gap-4 flex-wrap mt-4">
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                  <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-20" />
                </div>
              </div>
              <div className=""></div>

              <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-4 p-4">
                <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-40" />
                <hr className="mt-2" />
                <div className="flex gap-4 flex-wrap flex-col mt-4">
                  <div className="flex gap-2 items-center">
                    <div className="w-8 h-8 rounded-full bg-gray-200" />
                    <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="w-8 h-8 rounded-full bg-gray-200" />
                    <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="w-8 h-8 rounded-full bg-gray-200" />
                    <div className="h-6 bg-gray-200 rounded-full dark:bg-gray-700 w-64" />
                  </div>
                </div>
              </div>
              <div className=""></div>
            </div>
          ) : (
            searches.map((search) => (
              <QuickSearch key={search.title} group={search} />
            ))
          )}
        </main>
      </div>
    </>
  );
};

export default RemoteConfig;
